import { BaseUITools } from '@/uiControls/base/baseUITools'
// import i18NextClass from '../i18n/index.ts';

import { SplitPanelUI } from '@/uiControls/base/components/splitPanelNew.ts';
import { SceneViewPanelTools } from './panelControls/sceneview-panel-tools.ts';
import { modelDialogTools } from '@/utils/globalConfig.ts';
import { cameraSyncManager } from '@/visionaryControllers/cameraSyncManager';
import { getI18nText } from '@/utils/commonFunc.ts';

/**
 * 主视图工具类
 * 负责创建和管理主视图的分割面板，分面板可以添加sceneview-panel-tools，作为场景视图渲染容器
 */
export class MainViewTools extends BaseUITools {
    private id: string;
    private leftpanelId: string;
    private rightpanelId: string;
    private splitPanel!: SplitPanelUI;
    private sceneViewPanelLeftTools!: SceneViewPanelTools | null;
    private sceneViewPanelRightTools!: SceneViewPanelTools | null;

    constructor(id: string) {
        super(id, false);
        this.id = id;
        this.leftpanelId = '';
        this.rightpanelId = '';
        this.sceneViewPanelLeftTools = null;
        this.sceneViewPanelRightTools = null;
        this.init();
    }

    // 创建水平分隔面板
    async init() {
        this.splitPanel = new SplitPanelUI({
            direction: 'vertical',
            splitRatio: 50,
            minRatio: 1,
            maxRatio: 99,
            onResize: (ratio) => {
                this.handlePanelResize(ratio);
            }
        });

        this.appendChild('splitPanel', this.splitPanel.getContainer());

        // 初始化第一个面板
        try {
            await this.createNewPanel();
            console.log('初始化第一个面板成功');
            window.dispatchEvent(new CustomEvent('event.sceneViewPanelCreated'));
        } catch (error) {
            console.error('初始化第一个面板失败:', error);
        }
    }

    /**
     * 创建新面板
     * @returns 
     */
    public async createNewPanel(): Promise<void> {
        if (this.sceneViewPanelLeftTools && this.sceneViewPanelRightTools) {
            return;
        }
        // 生成6个随机字符或数字
        const randomStr = Math.random().toString(36).substring(2, 8).padEnd(6, '0');
        const sceneViewId = 'sceneViewPanel' + randomStr;

        const sceneViewPanel = await this.createSceneViewPanel(sceneViewId);
        if (!this.sceneViewPanelLeftTools) {
            this.leftpanelId = sceneViewId;
            this.sceneViewPanelLeftTools = sceneViewPanel;
        } else {
            this.rightpanelId = sceneViewId;
            this.sceneViewPanelRightTools = sceneViewPanel;
        }

        // 等待 DOM 渲染完成后再设置相机同步
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        // 设置相机同步
        await this.setupCameraSync();
        // 触发相机同步
        cameraSyncManager.syncCameras(this.sceneViewPanelLeftTools?.getCameraController());
        window.dispatchEvent(new CustomEvent('event.changeMultiView',{detail:{msg:"oncreate"}}));
    }

    /**
     * 删除面板
     * @param isLeft 是否删除左侧面板
     */
    public removePanel(isLeft: boolean): void {
        let removedSceneId = '';
        if (isLeft) {
            removedSceneId = this.leftpanelId;
            this.sceneViewPanelLeftTools?.destroy();
            this.splitPanel.removePanel(true);
            this.sceneViewPanelLeftTools = this.sceneViewPanelRightTools;
            this.leftpanelId = this.rightpanelId;
            this.rightpanelId = '';
            this.sceneViewPanelRightTools = null;
        } else {
            removedSceneId = this.rightpanelId;
            this.sceneViewPanelRightTools?.destroy();
            this.splitPanel.removePanel(false);
            this.sceneViewPanelRightTools = null;
            this.rightpanelId = '';
        }
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('event.changeMultiView',{detail:{msg:"onremove"}}));
            window.dispatchEvent(new Event('resize'));
        }, 500);
    }

    private async createSceneViewPanel(sceneViewId: string): Promise<SceneViewPanelTools> {
        // 创建容器元素
        const sceneViewPanelElement = this.ui.createElement({
            tag: 'div',
            className: 'vision-split-panel-body',
            id: sceneViewId,
        });

        // 先添加到 split panel，确保 DOM 元素已经渲染
        this.splitPanel.addPanel(sceneViewId, sceneViewPanelElement);

        // 等待 DOM 渲染完成
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        // 确保容器元素在 DOM 中存在后再创建 SceneViewPanelTools
        // 使用轮询等待 DOM 元素出现（最多等待 2 秒）
        let retryCount = 0;
        const maxRetries = 40; // 40 * 50ms = 2秒
        while (!document.getElementById(sceneViewId) && retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 50));
            retryCount++;
        }

        if (!document.getElementById(sceneViewId)) {
            throw new Error(`容器元素 ${sceneViewId} 在 DOM 中未找到，无法创建场景视图面板`);
        }

        // 创建场景视图面板工具
        const sceneViewPanel = new SceneViewPanelTools(sceneViewId);

        // 等待场景初始化完成
        try {
            await sceneViewPanel.waitForSceneInit();
            console.log(`场景视图面板 ${sceneViewId} 初始化完成`);
            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.finishInitOK'));
            modelDialogTools.closeLoadingInfoModal();
        } catch (error) {
            console.error(`场景视图面板 ${sceneViewId} 初始化失败:`, error);
            modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.finishInitFailed'));
            modelDialogTools.closeLoadingInfoModal(5);
            throw error;
        }

        return sceneViewPanel;
    }

    // 获取分隔面板实例
    getSplitPanel(): SplitPanelUI {
        return this.splitPanel;
    }

    /**
     * 处理分割面板尺寸变化
     */
    private handlePanelResize(ratio: number): void {
        // 延迟执行以确保 DOM 已更新
        setTimeout(() => {
            this.sceneViewPanelLeftTools?.getScene()?.onWindowResize();
            this.sceneViewPanelRightTools?.getScene()?.onWindowResize();
        }, 0);
    }

    /**
     * 设置相机同步
     */
    private async setupCameraSync(): Promise<void> {
        // 确保左侧场景工具存在
        if (!this.sceneViewPanelLeftTools) {
            console.warn('左侧场景视图工具未初始化，跳过相机同步');
            return;
        }

        try {
            // 等待左侧场景初始化（带超时）
            await Promise.race([
                this.sceneViewPanelLeftTools.waitForSceneInit(),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('左侧场景初始化超时')), 5000)
                )
            ]);
        } catch (error) {
            console.error('左侧场景初始化失败:', error);
            return;
        }
        const leftCameraController = this.sceneViewPanelLeftTools.getCameraController();

        if (!leftCameraController) {
            console.error('左侧相机控制器未初始化！');
            return;
        }
        // 注册左侧相机控制器到同步管理器
        cameraSyncManager.registerCamera(leftCameraController);
        // 如果右侧场景工具存在，等待其初始化并注册
        if (this.sceneViewPanelRightTools) {
            try {
                // 等待右侧场景初始化（带超时）
                await Promise.race([
                    this.sceneViewPanelRightTools.waitForSceneInit(),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('右侧场景初始化超时')), 5000)
                    )
                ]);
                const rightCameraController = this.sceneViewPanelRightTools.getCameraController();
                if (rightCameraController) {
                    cameraSyncManager.registerCamera(rightCameraController);
                    console.log('右侧相机控制器已注册到同步管理器');
                } else {
                    console.warn('右侧相机控制器未初始化');
                }
            } catch (error) {
                console.error('右侧场景初始化失败，仅使用左侧场景:', error);
            }
        }
        // 设置左侧相机为主相机
        cameraSyncManager.setMasterCamera(leftCameraController);
        // 初始化时调整视口尺寸
        await new Promise(resolve => setTimeout(resolve, 100));
        this.handlePanelResize(this.splitPanel.getRatio());
    }

    // /**
    //  * 设置相机模式（同时设置双视窗的相机）
    //  */
    // public setCameraMode(mode: 'orbit' | 'fps'): void {
    //     const leftController = this.sceneViewPanelLeftTools.getCameraController();
    //     const rightController = this.sceneViewPanelRightTools.getCameraController();

    //     leftController?.setCameraMode(mode);
    //     rightController?.setCameraMode(mode);
    // }


    /**
     * 加载场景
     * @param isLeftScene 是否加载左侧场景
     * @param sceneData 场景数据
     */
    public loadSceneByJson(isLeftScene: boolean, sceneData: any): void {
        if (isLeftScene) {
            this.sceneViewPanelLeftTools?.loadSceneByJson(sceneData);
        } else {
            this.sceneViewPanelRightTools?.loadSceneByJson(sceneData);
        }
    }

    /**
     * 获取左侧场景视图工具
     */
    public getLeftSceneViewTools(): SceneViewPanelTools | null {
        return this.sceneViewPanelLeftTools;
    }

    /**
     * 获取右侧场景视图工具
     */
    public getRightSceneViewTools(): SceneViewPanelTools | null {
        return this.sceneViewPanelRightTools;
    }
}